import React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CommonPopupBodyFooterLayout } from '../../global/common-popup/common-popup-body-footer-layout';
import { FormField } from '../../global/form/form-field/form-field';
import { RichTextEditor } from '../../global/rich-text-editor';
import { FORM_CONFIG, EmailTemplateFieldKey } from './email-templates-form.config';
import { TagsInput } from '../../global/tags-input/tags-input';
import { FormFieldInput } from '../../global/form/form-field-input/form-field-input';
import { FormFieldError } from '../../global/form/form-field-input/form-field-error';
import { DefaultCloseSubWindowButton } from '../../global/sub-window/common/common-close-sub-window-buttons';
import { MORE_BUTTON_TEXT, cancelText } from '../../../constants/common-labels.constants';
import { FormVmValueType } from '../../../infrastructure/form-viewmodel-controller';
import { DELETE_EMAIL_TEMPLATE_TEXT, EMAIL_TEMPLATE_IS_BEING_SAVED } from '../../_email-templates/email-templates.text';
import { EmailTemplatesFormVM } from './email-templates-form.viewmodel';

import './email-template-form.scss';
import OptionsButton, { IButtonOption } from '../../global/options-button/options-button';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../api/roles.dao';
import { EmailVariablesSection } from '../../global/email-variables/email-variables';
import { EMAIL_LIST_TYPE } from '../../../constants/email-variables.constants';

interface IProps {
    vm: EmailTemplatesFormVM;
}

const EmailTemplateFormBody = observer(({ vm }: IProps): JSX.Element => {
    const {
        name,
        type,
        description,
        to,
        replyTo,
        cc,
        bcc,
        emailSubject,
        emailBody
    } = vm.getFormFields(EmailTemplateFieldKey);

    return (
        <form className='email-template-form'>
            <div className='oe-form__row m-bottom-20px'>
                <FormFieldInput.Text
                    additionalClassName='w-40'
                    field={name}
                    labelDescriptor={FORM_CONFIG[EmailTemplateFieldKey.NAME].labelDescriptor}
                    withError
                />
                <FormFieldInput.Select
                    fieldClassName='w-40'
                    field={type}
                    labelDescriptor={FORM_CONFIG[EmailTemplateFieldKey.TYPE].labelDescriptor}
                    options={vm.getEmailTypes()}
                />
            </div>
            <div className='oe-form__row m-bottom-20px'>
                <FormFieldInput.Text
                    field={description}
                    labelDescriptor={FORM_CONFIG[EmailTemplateFieldKey.DESCRIPTION].labelDescriptor}
                    withError
                />
            </div>
            <div className='oe-form__row m-bottom-20px'>
                <FormField
                    label={<FormattedMessage {...FORM_CONFIG[EmailTemplateFieldKey.TO].labelDescriptor} />}
                    className='w-40'
                >
                    <TagsInput
                        tagsList={[]}
                        setSelectedTags={(email: FormVmValueType) => to.setValue(email)}
                        selectedTags={vm.getSelectedEmails(to)}
                        onBlur={to.setTouched}
                    />
                    <FormFieldError field={to} labelDescriptor={FORM_CONFIG[EmailTemplateFieldKey.TO].labelDescriptor} />
                </FormField>
                <FormField
                    label={<FormattedMessage {...FORM_CONFIG[EmailTemplateFieldKey.REPLY_TO].labelDescriptor} />}
                    className='w-40'
                >
                    <TagsInput
                        tagsList={[]}
                        setSelectedTags={(email: FormVmValueType) => replyTo.setValue(email)}
                        selectedTags={vm.getSelectedEmails(replyTo)}
                        onBlur={replyTo.setTouched}
                    />
                    <FormFieldError field={replyTo} labelDescriptor={FORM_CONFIG[EmailTemplateFieldKey.REPLY_TO].labelDescriptor} />
                </FormField>

            </div>
            <div className='oe-form__row m-bottom-20px'>
                <FormField
                    label={<FormattedMessage {...FORM_CONFIG[EmailTemplateFieldKey.CC].labelDescriptor} />}
                    className='w-40'
                >
                    <TagsInput
                        tagsList={[]}
                        setSelectedTags={(email: FormVmValueType) => cc.setValue(email)}
                        selectedTags={vm.getSelectedEmails(cc)}
                        onBlur={cc.setTouched}
                    />
                    <FormFieldError field={cc} labelDescriptor={FORM_CONFIG[EmailTemplateFieldKey.CC].labelDescriptor} />
                </FormField>

                <FormField
                    label={<FormattedMessage {...FORM_CONFIG[EmailTemplateFieldKey.BCC].labelDescriptor} />}
                    className='w-40'
                >
                    <TagsInput
                        tagsList={[]}
                        setSelectedTags={(email: FormVmValueType) => bcc.setValue(email)}
                        selectedTags={vm.getSelectedEmails(bcc)}
                        onBlur={bcc.setTouched}
                    />
                    <FormFieldError field={bcc} labelDescriptor={FORM_CONFIG[EmailTemplateFieldKey.BCC].labelDescriptor} />
                </FormField>
            </div>

            <EmailVariablesSection type={EMAIL_LIST_TYPE.CONNECTION}/>
            <FormFieldInput.Text
                field={emailSubject}
                labelDescriptor={FORM_CONFIG[EmailTemplateFieldKey.EMAIL_SUBJECT].labelDescriptor}
                additionalClassName='send-connections-email-form__subject'
                withError
            />
            <FormField
                label={<FormattedMessage {...FORM_CONFIG[EmailTemplateFieldKey.EMAIL_BODY].labelDescriptor} />}
                className='with-editor send-connections-email-form__email-body'
            >
                <RichTextEditor
                    className='m-top-0px body-editor'
                    value={emailBody.value as string}
                    onChange={action((event, editor) => {
                        emailBody.setValue(editor.getData());
                    })}
                    onBlur={emailBody.setTouched}
                />
                <FormFieldError field={emailBody} labelDescriptor={FORM_CONFIG[EmailTemplateFieldKey.EMAIL_BODY].labelDescriptor} />
            </FormField>
        </form>
    );
});

const EmailTemplateFormFooter = observer(({ submit, actionButtonText, isSaving, isEditMode, deleteConnectionEmail }): JSX.Element => {
    const intl = useIntl();

    const formOptions: IButtonOption[] = [
        isEditMode && hasPermission(Permission.DELETE_EMAIL_TEMPLATES) && {
            label: intl.formatMessage(DELETE_EMAIL_TEMPLATE_TEXT),
            action: deleteConnectionEmail,
            danger: true,
        },
    ].filter(Boolean);

    return (
        <>
            {isSaving && <span className='oe-popup-layout__footer-status-area'>
                <FormattedMessage {...EMAIL_TEMPLATE_IS_BEING_SAVED} />
            </span>}
            <div className='buttons-block buttons-block--from-right'>
                <DefaultCloseSubWindowButton
                    text={actionButtonText()}
                    modifier={['fat', 'confirm-safe']}
                    onClick={submit}
                    disabled={isSaving}
                />
                <DefaultCloseSubWindowButton text={cancelText()} />
                {!!formOptions.length && <OptionsButton
                        className='options-button--right-margin-auto'
                        modifier={['fat', 'options']}
                        label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                        options={formOptions}
                        position='top'
                        align='start'
                    />}
            </div>
        </>
    );
});

const EmailTemplateFormPresentation = observer(({ vm }: IProps): JSX.Element => {
    if (vm.isEditMode && !vm.emailSnapshot) return null;
    return (
        <CommonPopupBodyFooterLayout
            className='oe-form'
            body={<EmailTemplateFormBody vm={vm} />}
            footer={<EmailTemplateFormFooter
                isSaving={vm.saving}
                actionButtonText={vm.actionButtonText}
                submit={vm.submitForm}
                isEditMode={vm.isEditMode}
                deleteConnectionEmail={vm.deleteConnectionEmailAction}
            />}
        />
    );
});

export { EmailTemplateFormPresentation, EmailTemplateFormBody };
